import { Head, useForm, Link } from '@inertiajs/react'
import { PageProps } from '@/types'
import AppLayout from '@/layouts/AppLayout'
import { ArrowLeft } from 'lucide-react'
import { FormEvent } from 'react'

interface Carrier {
  id: number
  name: string
  code: string
  is_active: boolean
  carrier_payout_percentage: number
}

interface Props extends PageProps {
  carrier: Carrier | null
}

export default function CarrierForm({ carrier }: Props) {
  const isEdit = !!carrier

  const { data, setData, post, put, processing } = useForm({
    name: carrier?.name || '',
    code: carrier?.code || '',
    is_active: carrier?.is_active ?? true,
    carrier_payout_percentage: carrier?.carrier_payout_percentage ?? 100
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (isEdit) {
      put(`/carriers/${carrier.id}`)
    } else {
      post('/carriers')
    }
  }

  return (
    <AppLayout>
      <Head title={isEdit ? 'Edit Carrier' : 'New Carrier'} />

      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Link href="/carriers" className="p-2 text-chestnut-charcoal/60 hover:text-chestnut-forest">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">
            {isEdit ? 'Edit Carrier' : 'New Carrier'}
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="card space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-chestnut-charcoal mb-1">Carrier Name *</label>
              <input
                type="text"
                value={data.name}
                onChange={(e) => setData('name', e.target.value)}
                className="input-field w-full"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-chestnut-charcoal mb-1">Code *</label>
              <input
                type="text"
                value={data.code}
                onChange={(e) => setData('code', e.target.value)}
                className="input-field w-full"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-chestnut-charcoal mb-1">
              Carrier Payout Rate (%)
            </label>
            <div className="relative max-w-xs">
              <input
                type="number"
                value={data.carrier_payout_percentage}
                onChange={(e) => setData('carrier_payout_percentage', parseFloat(e.target.value) || 0)}
                className="input-field w-full pr-8"
                min="0"
                max="100"
                step="0.01"
                required
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-chestnut-charcoal/60">%</span>
            </div>
            <p className="text-xs text-chestnut-charcoal/60 mt-1">
              Percentage of the gross commission this carrier actually pays Chestnut.
              Defaults to 100% (carrier pays the full contracted rate). When the carrier pays less than the
              agent's contracted split, Chestnut covers the gap as a supplement.
            </p>
          </div>

          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={data.is_active}
                onChange={(e) => setData('is_active', e.target.checked)}
                className="rounded border-chestnut-charcoal/30 text-chestnut-forest focus:ring-chestnut-forest"
              />
              <span className="text-sm font-medium text-chestnut-charcoal">Active</span>
            </label>
          </div>

          <div className="flex justify-end gap-4 pt-4 border-t border-chestnut-gray">
            <Link href="/carriers" className="btn-secondary">Cancel</Link>
            <button type="submit" disabled={processing} className="btn-primary">
              {processing ? 'Saving...' : (isEdit ? 'Update Carrier' : 'Create Carrier')}
            </button>
          </div>
        </form>
      </div>
    </AppLayout>
  )
}
